import { Appointment } from '../types';
import { getTimeDifferenceInMinutes } from '@/utils/timeUtils';

export type ExternalEventSource = 'Google' | 'Outlook' | 'Zoom';

export const EXTERNAL_EVENT_SOURCES: ExternalEventSource[] = ['Google', 'Outlook', 'Zoom'];

/**
 * Minimum span (in minutes) at which an external calendar event is considered
 * to be blocking a meaningful chunk of the workday. Anchored at 4 hours per
 * the product rule of thumb for "all-day-ish" Google Calendar events that
 * silently eat availability.
 */
export const BLOCKING_EXTERNAL_EVENT_MINUTES = 4 * 60;

export interface BlockingExternalEvent {
  appointment: Appointment;
  durationMinutes: number;
  source: ExternalEventSource;
  isAllDay: boolean;
}

const isExternalSource = (source?: string): source is ExternalEventSource =>
  !!source && (EXTERNAL_EVENT_SOURCES as string[]).includes(source);

/**
 * Returns whether an appointment is an external (Google/Outlook/Zoom) event
 * whose span is long enough to materially block availability for the day.
 *
 * Backend transparency (busy/free) is not exposed on the timeline DTO today,
 * so we infer "this is probably an all-day busy block" from the duration.
 */
export const isBlockingExternalEvent = (apt: Appointment): boolean => {
  if (!isExternalSource(apt.source)) return false;
  if (!apt.startDateTime || !apt.endDateTime) return false;
  const durationMinutes = getTimeDifferenceInMinutes(apt.startDateTime, apt.endDateTime);
  return durationMinutes >= BLOCKING_EXTERNAL_EVENT_MINUTES;
};

/**
 * Heuristic: treat a span that covers ~24 hours (1440 minutes) or more as a
 * true "all day" event. Google Calendar all-day events typically come through
 * as midnight-to-midnight.
 */
export const isAllDayExternalEvent = (apt: Appointment): boolean => {
  if (!isExternalSource(apt.source)) return false;
  if (!apt.startDateTime || !apt.endDateTime) return false;
  const durationMinutes = getTimeDifferenceInMinutes(apt.startDateTime, apt.endDateTime);
  return durationMinutes >= 24 * 60;
};

/**
 * Filters a list of appointments down to long external events (>= 4 hours)
 * that are likely silently blocking availability for the user.
 */
export const findBlockingExternalEvents = (
  appointments: readonly Appointment[] | undefined | null
): BlockingExternalEvent[] => {
  if (!appointments) return [];
  const result: BlockingExternalEvent[] = [];
  for (const apt of appointments) {
    if (!isExternalSource(apt.source)) continue;
    if (!apt.startDateTime || !apt.endDateTime) continue;
    const durationMinutes = getTimeDifferenceInMinutes(apt.startDateTime, apt.endDateTime);
    if (durationMinutes < BLOCKING_EXTERNAL_EVENT_MINUTES) continue;
    result.push({
      appointment: apt,
      durationMinutes,
      source: apt.source,
      isAllDay: durationMinutes >= 24 * 60,
    });
  }
  return result;
};

/**
 * Returns true when an address looks like a physical location we can route to
 * (street address vs. a virtual meeting URL like Zoom / Teams / Meet).
 *
 * Used to decide whether a "blocked" event has a real location, or whether
 * Routemize will fall back to the previous appointment's address when
 * computing drive time for the next stop.
 */
export const isRoutableLocation = (address?: string | null): boolean => {
  if (!address) return false;
  const trimmed = address.trim().toLowerCase();
  if (!trimmed) return false;
  if (/^https?:\/\//.test(trimmed)) return false;
  if (/^(www\.)?\S+\.\S+\//.test(trimmed)) return false;
  if (/\b(zoom|teams|meet\.google|webex|goto)\b/i.test(trimmed)) return false;
  return true;
};

/**
 * True when the backend resolved this appointment's address into usable
 * coordinates. A 0,0 pair is treated as unresolved rather than as a point in
 * the Gulf of Guinea — the sync writes it when geocoding produced nothing.
 */
export const hasResolvedCoordinates = (apt: Appointment): boolean =>
  apt.latitude != null &&
  apt.longitude != null &&
  !(apt.latitude === 0 && apt.longitude === 0);

/**
 * Why an external event may not contribute to route math:
 * - `located`   — has an address that resolved to coordinates.
 * - `unlocated` — has an address string, but it never resolved. Typically a
 *                 free-text calendar location with only a street line, e.g.
 *                 "3944 Blackhawk Cir". Blocks time but drops out of travel
 *                 calculations, which skews the slots around it.
 * - `missing`   — no physical address at all (a Zoom link, or nothing).
 */
export type ExternalLocationState = 'located' | 'unlocated' | 'missing';

export const getExternalLocationState = (apt: Appointment): ExternalLocationState => {
  if (!isRoutableLocation(apt.address)) return 'missing';
  return hasResolvedCoordinates(apt) ? 'located' : 'unlocated';
};

const joinWithAnd = (items: readonly string[]): string =>
  items.length <= 1
    ? (items[0] ?? '')
    : `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;

/**
 * Names the address components a partial calendar location appears to be
 * missing, so a warning can say "add the city, state and ZIP" instead of the
 * unactionable "we couldn't find this address".
 *
 * Calendar locations are free text, so this is only a guess at the shape of
 * the string. It is meant to run *after* geocoding has already failed — it
 * explains a known failure rather than predicting one. Returns null when the
 * string looks complete enough that the gap is something else (a typo, a
 * business name, an address the geocoder simply doesn't carry).
 */
export const describeAddressGaps = (address?: string | null): string | null => {
  const trimmed = address?.trim();
  if (!trimmed) return null;

  const parts = trimmed.split(',').map((part) => part.trim()).filter(Boolean);
  const hasZip = /\b\d{5}(-\d{4})?\b/.test(trimmed);

  const gaps: string[] = [];
  if (parts.length < 2) gaps.push('city');
  if (parts.length < 3) gaps.push('state');
  if (!hasZip) gaps.push('ZIP');

  return gaps.length ? joinWithAnd(gaps) : null;
};

/**
 * Information about the location Routemize will treat as the effective
 * location of a blocked-time event when computing drive time for the next
 * stop. Either the previous physical stop, or — when the blocked event is
 * the first thing on the day — the user's configured starting location
 * (typically Home, sometimes Office or a custom starting address).
 */
export interface InheritedRoutingLocation {
  address: string;
  customerName?: string;
  appointmentId?: string;
  /**
   * True when the inherited location is the user's day-start location
   * (Home / Office / custom) rather than a previous customer stop.
   */
  isStartingLocation?: boolean;
  /** Type of the starting location, when `isStartingLocation` is true. */
  startingLocationType?: 'home' | 'office' | 'custom';
}

export interface FindInheritedRoutingLocationOptions {
  /**
   * Address of the user's day-start location (Home / Office / custom).
   * Used as the fallback when no earlier appointment in the day has a
   * routable physical address — for example when a Zoom event without an
   * address is the very first stop on the timeline.
   */
  startingAddress?: string | null;
  startingLocationType?: 'home' | 'office' | 'custom';
}

/**
 * Walks backward from `currentIndex` to find the most recent appointment with
 * a routable physical address. This mirrors the routing assumption made
 * upstream: a blocked-time event (e.g. a virtual call with no address)
 * inherits the previous physical stop as its effective location, so the next
 * appointment's drive time picks up where that stop left off.
 *
 * If no prior appointment has a physical address (e.g. the day starts with a
 * blocked event) and `options.startingAddress` is provided, returns that
 * starting address with `isStartingLocation: true`. Returns null only when
 * neither fallback is available.
 */
export const findInheritedRoutingLocation = (
  appointments: readonly Appointment[] | undefined | null,
  currentIndex: number,
  options?: FindInheritedRoutingLocationOptions
): InheritedRoutingLocation | null => {
  if (appointments) {
    for (let i = currentIndex - 1; i >= 0; i--) {
      const prev = appointments[i];
      if (!prev) continue;
      // Skip virtual / lunch placeholders — they're not real stops the user drove to.
      if (prev.id === 'virtual-start' || prev.id === 'virtual-end' || prev.id === 'lunch-break') continue;
      if (!isRoutableLocation(prev.address)) continue;
      return {
        address: prev.address,
        customerName: prev.customerName,
        appointmentId: prev.id,
      };
    }
  }
  // No earlier physical stop — fall back to the configured day-start location
  // so the user can see what Routemize is actually routing from.
  const startingAddress = options?.startingAddress?.trim();
  if (startingAddress && isRoutableLocation(startingAddress)) {
    return {
      address: startingAddress,
      isStartingLocation: true,
      startingLocationType: options?.startingLocationType,
    };
  }
  return null;
};
